// Provider errors arrive with raw JSON embedded. Parse enough to decide on a retry; never
// show the blob — it leaks quota metrics, model names and internal URLs.

const RETRYABLE_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504, 529])

const extractStatus = (message) => {
  const codeMatch = message.match(/"(?:code|status)"\s*:\s*(\d{3})/)

  if (codeMatch) {
    return Number(codeMatch[1])
  }

  const httpMatch = message.match(/HTTP Error:\s*(\d{3})/)

  return httpMatch ? Number(httpMatch[1]) : null
}

// Providers tell us how long to wait — Gemini as `"retryDelay": "58s"`, others in prose.
const extractRetryAfterMs = (message) => {
  const match = message.match(/"retryDelay"\s*:\s*"(\d+(?:\.\d+)?)s"/) ||
    message.match(/retry in\s+(\d+(?:\.\d+)?)\s*s/i)

  return match ? Math.ceil(Number(match[1]) * 1000) : null
}

export const classifyAiError = (error) => {
  const message = String(error?.message || '')
  const status = extractStatus(message)

  let kind = 'unknown'

  // Checked first: providers report this as a plain 400, which the status checks would miss.
  if (/context[_ ]length|maximum context|prompt is too long|input is too long|token count exceeds|too many (?:input )?tokens/i.test(message)) {
    kind = 'context_length'
  } else if (status === 429 || /RESOURCE_EXHAUSTED|rate limit|quota/i.test(message)) {
    kind = 'rate_limit'
  } else if (status === 401 || status === 403) {
    kind = 'auth'
  } else if (/Network request failed/i.test(message)) {
    kind = 'network'
  } else if (status && RETRYABLE_STATUSES.has(status)) {
    kind = 'overloaded'
  }

  return {
    kind,
    status,
    retryAfterMs: extractRetryAfterMs(message),
    retryable: ['rate_limit', 'network', 'overloaded'].includes(kind)
  }
}

const PROVIDER_BLOB = /AI Lambda service call failed|"error"\s*:\s*\{/

// Provider failures become friendly text; a domain rejection ("Field id already exists") survives.
export const sanitizeErrorMessage = (error) => {
  const message = String(error?.message || '')

  if (classifyAiError(error).kind !== 'unknown' || PROVIDER_BLOB.test(message)) {
    return friendlyAiMessage(error)
  }

  return message
}

export const friendlyAiMessage = (error) => {
  const { kind, retryAfterMs } = classifyAiError(error)
  const waitHint = retryAfterMs
    ? ` Try again in about ${Math.ceil(retryAfterMs / 1000)} seconds.`
    : ' Try again shortly.'

  switch (kind) {
    case 'rate_limit':
      return `The AI provider is rate limiting requests, or its quota is used up.${waitHint} You can also switch to a different model in the workflow settings.`
    case 'context_length':
      return 'This conversation has grown too long for the model. Clearing the chat history with the delete button at the top of this panel will let it continue.'
    case 'auth':
      return 'The AI service rejected our credentials. Please contact support.'
    case 'network':
      return 'Could not reach the AI service. Check your connection and try again.'
    case 'overloaded':
      return `The AI service is temporarily unavailable.${waitHint}`
    default:
      return 'The AI service returned an unexpected error. Please try again.'
  }
}
